import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { PaginationItems } from '@modules/table-pagination/interfaces/pagination-items.interface';
import { ServiceRequestFileInterface } from '@interfaces/service-request/service-request-file.interface';
import { FileResponse } from '../../../../../model/file.model';
import { FilesService } from '../../../../../service/files.service';
import { IAppCategoryListServerResponse } from '../../../../../service/category.model';
import { VendorService } from '../../../../../service/vendor.service';
import { FileUpdatePayloadInterface } from '../interfaces/file-update-payload.interface';

/**
 * Data access of the View Files modal: keeps the files API calls, the upload payload
 * and the browser download out of the component.
 */
@Injectable()
export class ViewFilesModalService {
  private readonly filesService = inject(FilesService);
  private readonly vendorService = inject(VendorService);

  /**
   * Loads a page of files of the given entity.
   *
   * @param entityTableName - entity the files belong to (`Itinary`, `Catering`, ...)
   * @param entityTableValue - id of the entity
   * @param page - requested page number
   * @param pageSize - requested page size
   */
  loadFiles(
    entityTableName: string,
    entityTableValue: string,
    page: number,
    pageSize: number,
  ): Observable<PaginationItems<ServiceRequestFileInterface>> {
    return this.filesService.fetchFiles(entityTableName, entityTableValue, page, pageSize);
  }

  /** Loads the file attachment tag options. */
  loadTagOptions(): Observable<IAppCategoryListServerResponse> {
    return this.filesService.getFileAttachmentTags();
  }

  /**
   * Uploads the newly picked files together with their attachment tags.
   *
   * @param entityTableName - entity the files belong to
   * @param entityTableValue - id of the entity
   * @param files - files picked in the modal, each one is a native `File` with the modal metadata attached
   */
  uploadFiles(entityTableName: string, entityTableValue: string, files: ServiceRequestFileInterface[]): Observable<FileResponse[]> {
    const formData = new FormData();
    const fileAttachmentTagIdList: { [key: string]: string } = {};
    let fileTypes = '';

    files.forEach((file) => {
      fileTypes = fileTypes.concat(file.fileType + ',');
      formData.append(file.displayName, file as unknown as Blob);
      fileAttachmentTagIdList[file.displayName] = file.fileAttachmentTagId;
    });

    formData.append('FileAttachmentTagIds', JSON.stringify(fileAttachmentTagIdList));
    fileTypes = fileTypes.replace(/,(?=\s*$)/, '');

    return this.filesService.uploadFile(fileTypes, entityTableName, entityTableValue, formData);
  }

  /**
   * Saves the name and the tag of already uploaded files.
   *
   * @param files - files to update
   */
  updateFiles(files: FileUpdatePayloadInterface[]): Observable<string> {
    return this.vendorService.editFile(files);
  }

  /**
   * Deletes an uploaded file.
   *
   * @param fileId - id of the file
   */
  deleteFile(fileId: string): Observable<FileResponse[]> {
    return this.filesService.deletedFile(fileId);
  }

  /**
   * Toggles the pinned state of a file.
   *
   * @param fileId - id of the file
   */
  togglePin(fileId: string): Observable<unknown> {
    return this.filesService.pinFile(fileId);
  }

  /**
   * Downloads a file through a temporary anchor element.
   *
   * @param file - file to download
   */
  downloadFile(file: ServiceRequestFileInterface): void {
    if (!file.fileName) {
      return;
    }

    const anchor = document.createElement('a');
    document.body.appendChild(anchor);
    anchor.href = file.filePath;
    anchor.download = file.displayName;
    anchor.click();
    document.body.removeChild(anchor);
  }
}
